import React, { useState, useReducer, forwardRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { motion } from 'framer-motion'
import Select, { components } from 'react-select'
import { Formik, useField } from 'formik'
import * as Yup from 'yup'
import Reaptcha from 'reaptcha'
import cn from 'classnames'
import countries from 'iso-3166-country-list'

import CoversWall from '../CoversWall'
import { Button } from '../controls'
import { loggedInResponse } from '../../localStorage'
import { MUTATION, QUERY } from '../../apollo'
import LogoPixedenMusic from '../LogoPixedenMusic'

import genre from '../filters/genre.json'
import vocals from '../filters/vocals.json'
import tempo from '../filters/tempo.json'
import language from '../filters/language.json'
import styles from './modal.module.css'

function SupervisionModal({ closeModal, asEmptyQuote }) {
  let history = useHistory()
  const { data } = useQuery(QUERY.CURRENT_USER)
  const currentUser = data?.currentUser

  return (
    <div className={styles.wrapper}>
      <CoversWall
        wrapperClassName={styles.coversWallWrapper}
        gridClassName={styles.coversWallGrid}
        animationDuration={0.5}
      />
      <div className={styles.containerOut}>
        <LogoPixedenMusic className={styles.logoPixedenMusic} />
        <div
          className={styles.contentWrapper}
          style={{ height: 'calc(100vh - 100px)' }}
        >
          <div className={cn(styles.sidebar, styles.noPlayer)}>
            {asEmptyQuote ? (
              <p
                className={cn(styles.text, styles.paragraph)}
                style={{ marginBottom: 52 }}
              >
                You have no music track selected.
              </p>
            ) : (
              <>
                <p className={cn(styles.text, styles.paragraph)}>
                  We offer musical supervision for your project.
                </p>
                <p className={cn(styles.text, styles.paragraph)}>
                  We can find your perfect soundtrack.
                </p>
                <p className={cn(styles.text, styles.paragraph)}>
                  We offer personalized music licensing service.
                </p>
                <p className={cn(styles.text, styles.paragraph)}>
                  Just let us know.
                </p>
              </>
            )}
          </div>
          <div className={cn(styles.mainSide, styles.noPlayer)}>
            <p
              className={styles.textSmall}
              style={{
                margin: '0.5em 0px 52px',
                fontSize: '20px',
                lineHeight: 1.5,
              }}
            >
              {asEmptyQuote
                ? 'Easily browse our music and select any track for a quote that fits your project. Need a custom free music selection? Just fill out the information below.'
                : 'Our professional music team is available to create a custom free song selection for you. Let us know a few details first.'}
            </p>
            <RequestForm
              recaptcha={true}
              initialValues={{
                ...(currentUser
                  ? { email: currentUser.email, name: currentUser.name }
                  : {}),
              }}
              callback={({ requestType, requestId }) => {
                setTimeout(() => {
                  closeModal()
                  history.push(`/profile/${requestType}/${requestId}`)
                }, 2000)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupervisionModal

const initialFormState = { response: false, error: false, errorMessage: '' }
function formReducer(state, action) {
  switch (action.type) {
    case 'ok':
      return {
        response: true,
        error: false,
        errorMessage: '',
      }
    case 'error':
      return {
        response: false,
        error: true,
        errorMessage: action.message,
      }
    case 'reset':
      return initialFormState
    default:
      throw new Error('Unexpected action type')
  }
}

export function RequestForm({
  recaptcha = false,
  callback = () => {},
  initialValues = {},
  hiddenValues = false,
  messageTime = 1000,
  requestType = 'supervision',
  extraValues = {},
  blueMode = false,
}) {
  const [formState, dispatch] = useReducer(formReducer, initialFormState)

  const [imNotARobot, setImNotARobot] = useState({
    token: null,
    verified: false,
  })

  const [askRequest] = useMutation(MUTATION.ASK_REQUEST, {
    onError: (error) => {
      console.log(error.graphQLErrors[0].message)
    },
    update(cache, { data: { askRequest } }) {
      const { token, refreshToken, user } = askRequest
      if (token && refreshToken) {
        loggedInResponse(cache, { accessToken: token, refreshToken, user })
      }
    },
    refetchQueries: [{ query: QUERY.CURRENT_USER }, { query: QUERY.USER_INFO }],
  })

  async function onSubmit(values) {
    try {
      if (!recaptcha || (recaptcha && imNotARobot.verified)) {
        const {
          data: { askRequest: askRequestResponse },
        } = await askRequest({
          variables: {
            input: {
              ...values,
              requestType,
              recaptchaToken: imNotARobot.token,
              media: values.media ? values.media.map((t) => t.value) : [],
              projectType: values.projectType
                ? values.projectType.map((t) => t.value)
                : [],
              territory: values.territory
                ? values.territory.map((t) => t.value)
                : [],
              vocals: values.vocals ? values.vocals.map((t) => t.value) : [],
              tempo: values.tempo ? values.tempo.map((t) => t.value) : [],
              language: values.language
                ? values.language.map((t) => t.value)
                : [],
              style: values.style ? values.style.map((t) => t.value) : [],
              country: values.country ? values.country.map((t) => t.value) : [],
              paidMedia: values.paidMedia ? values.paidMedia : '',
              typeOfUse: values.typeOfUse ? values.typeOfUse : '',
              licensingTerm: values.licensingTerm ? values.licensingTerm : '',
              typeOfLicense: values.typeOfLicense ? values.typeOfLicense : '',
              ...extraValues,
            },
          },
        })
        dispatch({ type: 'ok' })
        setTimeout(() => callback(askRequestResponse), messageTime)
      }
    } catch (err) {
      dispatch({ type: 'error', message: err.message })
    }
  }

  const validationSchema = createValidationSchema(requestType, hiddenValues)

  return formState.response ? (
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      key="responseok"
      className={styles.text}
      style={{ color: 'var(--c-red)', fontSize: '1.7em' }}
    >
      Information sent successfully!
    </motion.p>
  ) : (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      key="form"
    >
      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={{
          style: '',
          reference: '',
          ...initialValues,
        }}
        validateOnBlur
        validateOnChange
      >
        {({
          isSubmitting,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          values,
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={cn(styles.grid, styles.form, {
                [styles.blue]: blueMode,
              })}
            >
              <InputField
                name="email"
                placeholder="&nbsp;Email"
                type={hiddenValues ? 'hidden' : 'email'}
              />
              <InputField
                name="name"
                placeholder="&nbsp;Full Name"
                type={hiddenValues ? 'hidden' : 'text'}
              />
              <InputField
                name="company"
                placeholder="&nbsp;Company"
                type={hiddenValues ? 'hidden' : 'text'}
              />
              <InputField name="projectName" placeholder="&nbsp;Project Name" />
              <InputField
                name="licenseUser"
                placeholder="&nbsp;Licensee"
                label="End Client"
              />
              {requestType === 'supervision' && (
                <SelectField
                  name="style"
                  options={STYLE_OPTS}
                  placeholder="Music Style"
                  onChange={(value) => setFieldValue('style', value)}
                  onBlur={() => setFieldTouched('style', true)}
                  isMulti
                />
              )}
              {requestType === 'supervision' && (
                <>
                  <SelectField
                    name="vocals"
                    options={VOCALS_OPTS}
                    placeholder="&nbsp;Vocals"
                    onChange={(value) => setFieldValue('vocals', value)}
                    onBlur={() => setFieldTouched('vocals', true)}
                    isSearchable={false}
                    isMulti
                  />
                  <SelectField
                    name="tempo"
                    options={TEMPO_OPTS}
                    placeholder="&nbsp;Tempo"
                    onChange={(value) => setFieldValue('tempo', value)}
                    onBlur={() => setFieldTouched('tempo', true)}
                    isSearchable={false}
                    isMulti
                  />
                  <SelectField
                    name="language"
                    options={LANGUAGE_OPTS}
                    placeholder="&nbsp;Language"
                    onChange={(value) => setFieldValue('language', value)}
                    onBlur={() => setFieldTouched('language', true)}
                    isSearchable={false}
                    isMulti
                  />
                  <InputField
                    name="reference"
                    placeholder="&nbsp;Reference"
                    label="link youtube, link spotify"
                  />
                </>
              )}
              <SelectField
                name="media"
                options={MEDIA_OPTS}
                placeholder="Media"
                onChange={(value) => setFieldValue('media', value)}
                onBlur={() => setFieldTouched('media', true)}
                isMulti
              />
              {values.media &&
                values.media.some(
                  (value) => value.label === 'Online / Web / Streaming'
                ) && (
                  <SelectField
                    name="paidMedia"
                    options={PAIDMEDIA_OPTS}
                    placeholder="&nbsp;Paid Media"
                    onChange={({ value }) => setFieldValue('paidMedia', value)}
                    onBlur={() => setFieldTouched('paidMedia', true)}
                    isSearchable={false}
                  />
                )}
              <SelectField
                name="projectType"
                options={PROJECT_TYPE_OPTS}
                placeholder="&nbsp;Type of Project"
                onChange={(value) => setFieldValue('projectType', value)}
                onBlur={() => setFieldTouched('projectType', true)}
                isMulti
              />
              <SelectField
                name="territory"
                options={TERRITORY_OPTS}
                placeholder="&nbsp;Territory"
                onChange={(value) => setFieldValue('territory', value)}
                onBlur={() => setFieldTouched('territory', true)}
                isSearchable={false}
                isMulti
              />
              {values.territory &&
                values.territory.some(
                  (value) => value.label === 'National'
                ) && (
                  <SelectField
                    name="country"
                    options={COUNTRIES_OPTS}
                    placeholder="&nbsp;Country"
                    onChange={(value) => setFieldValue('country', value)}
                    onBlur={() => setFieldTouched('country', true)}
                    isMulti
                  />
                )}
              <SelectField
                name="licensingTerm"
                options={LICENSINGTERM_OPTS}
                placeholder="&nbsp;Licensing Term"
                onChange={({ value }) => setFieldValue('licensingTerm', value)}
                onBlur={() => setFieldTouched('licensingTerm', true)}
                isSearchable={false}
              />
              <InputField name="budget" placeholder="&nbsp;Budget" />
              <SelectField
                name="typeOfUse"
                options={TYPEOFUSE_OPTS}
                placeholder="&nbsp;Type of Use"
                onChange={({ value }) => setFieldValue('typeOfUse', value)}
                onBlur={() => setFieldTouched('typeOfUse', true)}
                isSearchable={false}
              />
              <SelectField
                name="typeOfLicense"
                options={TYPEOFLICENSE_OPTS}
                placeholder="&nbsp;Type of License"
                onChange={({ value }) => setFieldValue('typeOfLicense', value)}
                value={values['typeOfLicense']}
                onBlur={() => setFieldTouched('typeOfLicense', true)}
                isSearchable={false}
              />
              <TextareaField
                name="extraInfo"
                placeholder="&nbsp;Extra information"
                label="Tell us about your project"
              />
              {recaptcha && (
                <Reaptcha
                  className={cn(styles.recaptcha, {
                    [styles.noSpace]: requestType === 'supervision',
                  })}
                  sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                  onVerify={(token) => {
                    setImNotARobot({ verified: true, token })
                  }}
                  onExpire={() => {
                    setImNotARobot({ verified: false, token: null })
                  }}
                />
              )}
              <footer className={styles.formFooter}>
                <Button
                  className={styles.button}
                  type="submit"
                  disabled={
                    recaptcha
                      ? !imNotARobot.verified || isSubmitting
                      : isSubmitting
                  }
                >
                  Send
                </Button>
                {formState.error ? (
                  formState.errorMessage ? (
                    <p>{formState.errorMessage}</p>
                  ) : (
                    <p>Something went wrong</p>
                  )
                ) : null}
              </footer>
            </form>
          )
        }}
      </Formik>
    </motion.div>
  )
}

export const createValidationSchema = (type = 'supervision', hiddenValues) => {
  return Yup.object().shape({
    email: Yup.string().email().required('Email is Required'),
    name: Yup.string().required('Full Name is Required'),
    company: Yup.string().required(hiddenValues ? null : 'Company is Required'),
    projectName: Yup.string().required('Project Name is Required'),
    // licenseUser: Yup.string().required('Required'),
    media: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable()
      .required('Pick at least 1 Paid Media'),
    projectType: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable()
      .required('Pick at least 1 Project Type'),
    territory: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable()
      .required('Pick at least 1 Region'),
    licensingTerm: Yup.string().required('License Term is Required'),
    typeOfUse: Yup.string().required('Type of Use is Required'),
    typeOfLicense: Yup.string().required('Type of License is Required'),
    style: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .required('Pick at least 1 Music Style')
      .when('type', (_t, schema) =>
        type === 'supervision' ? schema.required() : schema.optional()
      )
      .nullable(),
    vocals: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable()
      .required('Pick at least 1 Vocals')
      .when('type', (_t, schema) =>
        type === 'supervision' ? schema.required() : schema.optional()
      ),
    tempo: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
      )
      .nullable()
      .required('Pick at least 1 Tempo')
      .when('type', (_t, schema) =>
        type === 'supervision' ? schema.required() : schema.optional()
      ),
  })
}

export function SelectField({ label, labelStyle, ...props }) {
  const [field, meta] = useField(props)
  const defaultValue = (options, value) => {
    return options ? options.find((option) => option.value === value) : ''
  }
  return (
    <label className={styles.label} style={labelStyle}>
      <p className={styles.inputLabel}>
        {label || props.placeholder}
        {meta.error ? (
          <span className={styles.errorMsg}>{meta.error}</span>
        ) : null}
      </p>
      <Select
        className={styles.select}
        components={{
          DropdownIndicator: (props) => {
            return (
              <components.DropdownIndicator {...props}>
                <svg viewBox="0 0 13 9" width="11" height="7">
                  <path
                    fillRule="evenodd"
                    fill="#141520"
                    d="M6.496 8.847L.37 1.8A1.076 1.076 0 01.438.324a.97.97 0 011.414.072l4.644 5.34 4.645-5.34a.97.97 0 011.414-.072c.41.388.44 1.049.068 1.476L6.496 8.847z"
                  />
                </svg>
              </components.DropdownIndicator>
            )
          },
        }}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor:
              state.menuIsOpen || state.isFocused
                ? 'var(--c-red)'
                : 'transparent',
            '&:hover': {
              borderColor:
                state.menuIsOpen || state.isFocused
                  ? 'var(--c-red)'
                  : 'transparent',
            },
            minHeight: 48,
            backgroundColor:
              state.menuIsOpen || state.isFocused ? '#fff' : 'var(--bg)',
            borderRadius: state.menuIsOpen ? '1.4em' : '2em',
            borderWidth: 1,
            borderBottomColor: state.menuIsOpen
              ? '#fff'
              : state.isFocused
              ? 'var(--c-red)'
              : 'transparent',
            borderBottomLeftRadius: state.menuIsOpen ? 0 : '2em',
            borderBottomRightRadius: state.menuIsOpen ? 0 : '2em',
            marginTop: 2,
            boxShadow:
              state.menuIsOpen || state.isFocused
                ? '0 0 0 4px var(--bg)'
                : 'none',
          }),
          container: (base) => ({
            ...base,
            marginBottom: 0,
            lineHeight: 1,
          }),
          placeholder: (base, state) => ({
            ...base,
            color: state.isFocused ? '#9a9ba4' : '#9a9ba4',
            paddingLeft: '0.25em',
            marginTop: 2,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            paddingRight: '9px',
          }),
          indicatorSeparator: () => ({ display: 'none' }),
          singleValue: (base) => ({ ...base, color: '#141520' }),
          valueContainer: (base) => ({
            ...base,
            paddingLeft: '1em',
          }),
          multiValue: (base, state) => ({
            ...base,
            backgroundColor: state.menuIsOpen ? '#ececf1' : '#fff',
            padding: '0.3em',
            color: '#141520',
            borderRadius: '2em',
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: '#141520',
            marginRight: 4,
          }),
          multiValueRemove: (base) => ({
            ...base,
            color: '#9a9ab0',
            backgroundColor: 'transparent',
            padding: '2px',
            '&:hover': {
              color: '#141520',
              backgroundColor: 'transparent',
            },
          }),
          menu: (base, state) => ({
            ...base,
            backgroundColor: '#fff',
            color: 'rgba(255, 255, 255, 0.5)',
            marginTop: -1,
            borderRadius: '1.4em',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            zIndex: 2,
            border: '1px solid var(--c-red)',
            boxShadow:
              '0 4px 0 0px var(--bg), 4px 0px 0 0px var(--bg), -4px 0px 0 0px var(--bg)',
            borderTop: 0,
          }),
          menuList: (base) => ({
            ...base,
            borderTopColor: 'transparent',
            zIndex: 1,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 20,
            borderBottomRightRadius: 20,
            paddingBottom: 'calc(1em + 16px)',
            transition: 'none',
          }),
          option: (base, state) => ({
            ...base,
            color: '#141520',
            width: 'calc(100% - 36px)',
            margin: '0 auto',
            borderRadius: '2em',
            backgroundColor: state.isFocused ? '#ececf1' : 'transparent',
            lineHeight: 1.3,
            '&:hover': {
              backgroundColor: '#ececf1',
            },
          }),
        }}
        {...field}
        {...props}
        value={defaultValue(props.options, field.value)}
      />
    </label>
  )
}

export const InputField = forwardRef(
  ({ label, labelStyle, children, ...props }, ref) => {
    const [field, meta] = useField(props)
    return (
      <label
        className={cn(styles.label, {
          [styles.hidden]: props.type === 'hidden',
        })}
        style={labelStyle}
      >
        <p className={styles.inputLabel}>
          {props.placeholder || label}{' '}
          {meta.error ? (
            <span className={styles.errorMsg}>{meta.error}</span>
          ) : null}
        </p>
        <input
          className={cn(styles.input, {
            [styles.inputError]: meta.touched && meta.error,
            [styles.notEmpty]: meta.value,
          })}
          ref={ref}
          {...field}
          {...props}
        />
        {children}
      </label>
    )
  }
)

export function TextareaField({ label, ...props }) {
  const [field, meta] = useField(props)
  return (
    <label className={styles.label}>
      <p className={styles.inputLabel}>
        {props.placeholder || label}
        {meta.error ? (
          <span className={styles.errorMsg}>{meta.error}</span>
        ) : null}
      </p>
      <textarea
        className={cn(styles.input, styles.textarea, {
          [styles.inputError]: meta.touched && meta.error,
        })}
        {...field}
        {...props}
      />
    </label>
  )
}

export const MEDIA_OPTS = [
  {
    label: 'Online / Web / Streaming',
    value: 'Online / Web / Streaming',
  },
  { label: 'Tv', value: 'Tv' },
  { label: 'Cinema', value: 'Cinema' },
  { label: 'Film Festival', value: 'Film Festival' },
  { label: 'Social Media', value: 'Social Media' },
  { label: 'In Store', value: 'In Store' },
  { label: 'Event & Trade Show', value: 'Event & Trade Show' },
  { label: 'Corporate Internal', value: 'Corporate Internal' },
  { label: 'Radio', value: 'Radio' },
  { label: 'Podcast', value: 'Podcast' },
  { label: 'Other', value: 'Other' },
]

const PAIDMEDIA_OPTS = [
  { label: 'Paid Media', value: 'Paid Media' },
  {
    label: 'Without Paid Media',
    value: 'Without Paid Media',
  },
]

export const PROJECT_TYPE_OPTS = [
  { label: 'Advertising', value: 'Advertising' },
  { label: 'Branded Content', value: 'Branded Content' },
  { label: 'Serie', value: 'Serie' },
  { label: 'Feature Film', value: 'Feature Film' },
  { label: 'Independent Film', value: 'Independent Film' },
  { label: 'Short Film', value: 'Short Film' },
  { label: 'Trailer', value: 'Trailer' },
  { label: 'Reel', value: 'Reel' },
  { label: 'Teaser, Intro-Video', value: 'Teaser, Intro-Video' },
  { label: 'Video Game', value: 'Video Game' },
  { label: 'Other', value: 'Other' },
]

export const TERRITORY_OPTS = [
  { label: 'Worldwide', value: 'Worldwide' },
  { label: 'National', value: 'National' },
  { label: 'Regional', value: 'Regional' },
  { label: 'Local', value: 'Local' },
]

const TYPEOFUSE_OPTS = [
  { label: 'Public Use', value: 'Public Use' },
  { label: 'Private Use', value: 'private' },
]

const LICENSINGTERM_OPTS = [
  { label: '1 Month', value: '1 Month' },
  { label: '6 Months', value: '6 Months' },
  { label: '1 Year', value: '1 Year' },
  { label: 'Perpetual', value: 'Perpetual' },
]

const TYPEOFLICENSE_OPTS = [
  { label: 'Non-Exclusive License', value: 'Non-Exclusive License' },
  { label: 'Exclusive License', value: 'Exclusive License' },
]

export const COUNTRIES_OPTS = countries.map(({ code, name }) => ({
  label: name,
  value: code,
}))

const STYLE_OPTS = genre.sort().map((g) => ({ label: g, value: g }))
const VOCALS_OPTS = vocals.map((v) => ({ label: v, value: v }))
const TEMPO_OPTS = tempo.map((t) => ({ label: t, value: t }))
const LANGUAGE_OPTS = language.map((t) => ({ label: t, value: t }))
